import lodash from 'lodash'
import { pi } from 'mathjs'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoLeakDetectionValve,
  GeckoLeakDetectionValveJSON,
} from 'types/assetModel/features/geckoLeakDetectionValve'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export type GeckoVerticalTankFoundationJSON = {
  name: string
  type: GeckoComponentModelType.VerticalTankFoundation
  height: number
  radius: number
  material: string | undefined
  installation_date: string | undefined
  features: Record<string, GeckoLeakDetectionValveJSON>
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
  compass_orientation: number | undefined
  design: string | undefined
}

export const VerticalTankFoundationAvailbleFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.LeakDetectionValve,
]

export const NewVerticalTankFoundationComponentJSON = {
  name: 'New Vertical Tank Foundation',
  type: GeckoComponentModelType.VerticalTankFoundation,
  height: 0,
  radius: 0,
  material: undefined,
  installation_date: undefined,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  compass_orientation: undefined,
  design: undefined,
} as GeckoVerticalTankFoundationJSON

export class GeckoVerticalTankFoundation extends GeckoEditableComponent {
  type: GeckoComponentModelType.VerticalTankFoundation
  radius: GeckoMeasurement
  height: GeckoMeasurement
  material: string | undefined
  installationDate: Date | undefined
  jointEfficiency: number | undefined
  sceneConfig = undefined
  componentBounds: GeckoComponentRect
  leakDetectionValves: Map<string, GeckoLeakDetectionValve>
  compassOrientation: number | undefined
  design: string | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    height: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    material: string | undefined,
    installationDate: Date | undefined,
    leakDetectionValves: Map<string, GeckoLeakDetectionValve>,
    compassOrientation: number | undefined,
    design: string | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.VerticalTankFoundation
    this.radius = radius
    this.height = height
    this.material = material
    this.installationDate = installationDate
    this.leakDetectionValves = leakDetectionValves
    this.compassOrientation = compassOrientation
    this.componentBounds = componentBounds
    this.design = design
  }

  public get leakDetectionValvesArray() {
    return Array.from(this.leakDetectionValves.values())
  }

  public static CreateFromJSON(
    id: string,
    json: GeckoVerticalTankFoundationJSON
  ) {
    const name = json.name
    const foundationRadius = GeckoMeasurement.CreateFromValue(
      json.radius,
      GeckoMeasurementUnit.Meters
    )
    const foundationHeight = GeckoMeasurement.CreateFromValue(
      json.height,
      GeckoMeasurementUnit.Meters
    )
    const material = json.material
    const installationDate = parseDate(json.installation_date)

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const featureData =
      json.features as GeckoVerticalTankFoundationJSON['features']

    const childValveJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.LeakDetectionValve
    })
    const leakDetectionValves = GeckoLeakDetectionValve.CreateMapFromJSON(
      id,
      childValveJSON as Record<string, GeckoLeakDetectionValveJSON>
    )

    const compassOrientation = parseNumber(json.compass_orientation)
    const design = parseString(json.design)

    return new GeckoVerticalTankFoundation(
      id,
      json,
      name,
      foundationRadius,
      foundationHeight,
      componentBounds,
      material,
      installationDate,
      leakDetectionValves,
      compassOrientation,
      design
    )
  }

  public asJSON(): GeckoVerticalTankFoundationJSON {
    return generateVerticalTankFoundation(
      this.name,
      this.height,
      this.radius,
      this.json as GeckoVerticalTankFoundationJSON,
      this.material,
      this.installationDate,
      this.compassOrientation,
      this.design
    )
  }
}

const generateVerticalTankFoundation = (
  name: string,
  height: GeckoMeasurement,
  radius: GeckoMeasurement,
  otherVerticalFoundationData: GeckoVerticalTankFoundationJSON = NewVerticalTankFoundationComponentJSON,
  material: string | undefined,
  installationDate: Date | undefined,
  compassOrientation: number | undefined,
  design: string | undefined
): GeckoVerticalTankFoundationJSON => {
  const tankInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined
  const tankHeight = height
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tankRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tankCircumference = Number(
    (2 * pi * tankRadius).toPrecision(assetModelPrecision)
  )

  const featureSetToUse: GeckoVerticalTankFoundationJSON['features'] = {
    ...otherVerticalFoundationData.features,
  }

  return {
    name: name,
    type: GeckoComponentModelType.VerticalTankFoundation,
    material: material,
    installation_date: tankInstallationDate,
    height: tankHeight,
    radius: tankRadius,
    features: featureSetToUse,
    scene_config: otherVerticalFoundationData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: tankCircumference,
      y_end: tankHeight,
    },
    compass_orientation: compassOrientation,
    design: design,
  }
}

export const VerticalTankFoundationFormFields = (
  verticalTankFoundationJSON: GeckoVerticalTankFoundationJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: verticalTankFoundationJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: verticalTankFoundationJSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: verticalTankFoundationJSON.height,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: verticalTankFoundationJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'installation_date',
      label: 'Installation Date',
      value: verticalTankFoundationJSON.installation_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Date,
    },
    {
      dataKey: 'compass_orientation',
      label: 'Compass Orientation',
      value: verticalTankFoundationJSON.compass_orientation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'design',
      label: 'Design',
      value: verticalTankFoundationJSON.design,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
  ]
}

export const VerticalTankFoundationJSONFromFormFields = (
  component: GeckoVerticalTankFoundationJSON,
  formFields: FormField[]
) => {
  const material = getFormFieldByDataKey(formFields, 'material')
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const compassOrientation = getFormFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  const design = getFormFieldByDataKey(formFields, 'design')
  return generateVerticalTankFoundation(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
      GeckoMeasurementUnit.Meters
    ),
    component,
    material?.value ? (material?.value as string) : undefined,
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parseNumber(compassOrientation?.value),
    parseString(design?.value)
  )
}
